import React from 'react';
import PropTypes from 'prop-types';
import posthog from 'posthog-js';
import Page500 from 'src/pages/500';
import ThemeProvider from 'src/theme';
import { SettingsProvider, SettingsDrawer } from 'src/components/settings';
import { isChunkLoadError, reloadForStaleChunk } from 'src/components/app-update';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reloading: false };
    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError(error) {
    // A page from before a deploy asking for a route chunk the deploy removed. Show
    // nothing while componentDidCatch reloads it onto the new version.
    return { hasError: true, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    console.log(error, errorInfo);
    // React swallows render errors it catches here, so exception autocapture
    // never sees them. Report them explicitly.
    if (posthog.__loaded) {
      posthog.captureException(error, {
        source: 'error_boundary',
        component_stack: errorInfo?.componentStack,
      });
    }
    if (isChunkLoadError(error) && !reloadForStaleChunk()) {
      // Already reloaded moments ago and the chunk is still missing: a real fault.
      this.setState({ reloading: false });
    }
  }

  resetError() {
    this.setState({ hasError: false, reloading: false });
  }

  render() {
    const { hasError, reloading } = this.state;
    const { children } = this.props;

    if (hasError && reloading) return null;

    if (hasError) {
      return (
        <SettingsProvider>
          <ThemeProvider>
            <SettingsDrawer />
            <Page500 resetError={this.resetError} />
          </ThemeProvider>
        </SettingsProvider>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};
